import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { NEWS, NEWS_CATEGORY_LABELS } from "@/mocks/news";
import { MEDIA, TYPE_LABELS } from "@/mocks/media";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = NEWS.find((n) => n.id === id);
  const relatedMedia = item?.relatedMediaId ? MEDIA.find((m) => m.id === item.relatedMediaId) : undefined;

  usePageMeta({
    title: item ? `${item.title} - WWW.17C影视资讯` : "影视资讯 - WWW.17C",
    description: item ? item.summary : "WWW.17C 影视资讯详情页。",
    keywords: item ? `${item.title},影视资讯,WWW.17C` : "影视资讯,WWW.17C",
  });

  const moreNews = useMemo(() => {
    if (!item) return [];
    const others = NEWS.filter((n) => n.id !== item.id);
    const sameCategory = others.filter((n) => n.category === item.category);
    const differentCategory = others.filter((n) => n.category !== item.category);
    return [...sameCategory, ...differentCategory].slice(0, 4);
  }, [item]);

  if (!item) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="该资讯不存在或已被删除"
          description="你访问的资讯可能已下线，或链接有误。"
          actionLabel="返回资讯列表"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/news");
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1000px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "影视资讯", to: "/news" }, { label: item.title }]}
      />

      <article className="mt-5">
        <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
          <span className="rounded-full bg-accent-100 px-3 py-1 font-medium text-accent-700">
            {NEWS_CATEGORY_LABELS[item.category]}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {item.publishedAt}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-newspaper-line"></i>
            来源：{item.source}
          </span>
        </div>

        <h1 className="mt-4 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
          {item.title}
        </h1>
        <p className="mt-4 rounded-lg border-l-4 border-accent-500 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
          {item.summary}
        </p>

        <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - WWW.17C`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {item.content.map((para, i) => (
            <p key={i} className="text-base leading-loose text-foreground-800">
              {para}
            </p>
          ))}
        </div>

        {relatedMedia && (
          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
            <p className="mb-3 text-sm text-foreground-500">相关作品</p>
            <Link to={`/detail/${relatedMedia.id}`} className="group flex items-center gap-4">
              <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={relatedMedia.cover}
                  alt={relatedMedia.title}
                  title={`${relatedMedia.title} - WWW.17C`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {relatedMedia.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">
                  {TYPE_LABELS[relatedMedia.type]} · {relatedMedia.year} · {relatedMedia.genres.join(" / ")}
                </p>
                <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{relatedMedia.summary}</p>
              </div>
            </Link>
          </div>
        )}
      </article>

      {/* 相关资讯 */}
      <section className="mt-12">
        <h2 className="mb-5 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
          <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
          更多资讯
        </h2>
        <div className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
          {moreNews.map((n) => (
            <Link
              key={n.id}
              to={`/news/${n.id}`}
              className="group flex items-center justify-between gap-4 p-4 transition-colors hover:bg-background-100"
            >
              <div className="min-w-0">
                <h3 className="line-clamp-1 font-heading text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                  {n.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">
                  {NEWS_CATEGORY_LABELS[n.category]} · {n.publishedAt}
                </p>
              </div>
              <i className="ri-arrow-right-s-line shrink-0 text-xl text-foreground-400"></i>
            </Link>
          ))}
        </div>
        <div className="mt-6">
          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
        </div>
      </section>
    </div>
  );
}